import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Search, Smartphone, Check, X, ChevronDown, AlertTriangle, Wifi, Signal } from 'lucide-react';

// Comprehensive eSIM compatible device database
const ESIM_DEVICES = {
  Apple: [
    { model: 'iPhone 16 Pro Max', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'iPhone 16 Pro', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'iPhone 16 Plus', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'iPhone 16', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'iPhone 15 Pro Max', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'iPhone 15 Pro', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'iPhone 15 Plus', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'iPhone 15', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'iPhone 14 Pro Max', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'iPhone 14 Pro', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'iPhone 14 Plus', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'iPhone 14', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'iPhone SE (3rd gen)', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'iPhone 13 Pro Max', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'iPhone 13 Pro', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'iPhone 13 mini', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'iPhone 13', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'iPhone 12 Pro Max', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'iPhone 12 Pro', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'iPhone 12 mini', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'iPhone 12', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'iPhone SE (2nd gen)', esim: true, dual: true, fiveG: false, year: 2020 },
    { model: 'iPhone 11 Pro Max', esim: true, dual: true, fiveG: false, year: 2019 },
    { model: 'iPhone 11 Pro', esim: true, dual: true, fiveG: false, year: 2019 },
    { model: 'iPhone 11', esim: true, dual: true, fiveG: false, year: 2019 },
    { model: 'iPhone XS Max', esim: true, dual: true, fiveG: false, year: 2018 },
    { model: 'iPhone XS', esim: true, dual: true, fiveG: false, year: 2018 },
    { model: 'iPhone XR', esim: true, dual: true, fiveG: false, year: 2018 },
    { model: 'iPad Pro (M4)', esim: true, dual: false, fiveG: true, year: 2024 },
    { model: 'iPad Air (M2)', esim: true, dual: false, fiveG: true, year: 2024 },
    { model: 'iPad (10th gen)', esim: true, dual: false, fiveG: true, year: 2022 },
    { model: 'iPad mini (6th gen)', esim: true, dual: false, fiveG: true, year: 2021 },
    { model: 'Apple Watch Ultra 2', esim: true, dual: false, fiveG: false, year: 2023 },
    { model: 'Apple Watch Series 9', esim: true, dual: false, fiveG: false, year: 2023 },
  ],
  Samsung: [
    { model: 'Galaxy S25 Ultra', esim: true, dual: true, fiveG: true, year: 2025 },
    { model: 'Galaxy S25+', esim: true, dual: true, fiveG: true, year: 2025 },
    { model: 'Galaxy S25', esim: true, dual: true, fiveG: true, year: 2025 },
    { model: 'Galaxy S24 Ultra', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Galaxy S24+', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Galaxy S24', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Galaxy S23 Ultra', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Galaxy S23+', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Galaxy S23', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Galaxy S22 Ultra', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'Galaxy S22+', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'Galaxy S22', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'Galaxy S21 Ultra 5G', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'Galaxy S21+ 5G', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'Galaxy S21 5G', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'Galaxy S20 Ultra 5G', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'Galaxy S20+ 5G', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'Galaxy S20 5G', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'Galaxy Z Fold 6', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Galaxy Z Flip 6', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Galaxy Z Fold 5', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Galaxy Z Flip 5', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Galaxy Z Fold 4', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'Galaxy Z Flip 4', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'Galaxy Z Fold 3', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'Galaxy Z Flip 3', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'Galaxy Note 20 Ultra 5G', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'Galaxy Note 20 5G', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'Galaxy A55 5G', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Galaxy A54 5G', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Galaxy A35 5G', esim: true, dual: true, fiveG: true, year: 2024 },
  ],
  Google: [
    { model: 'Pixel 9 Pro XL', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Pixel 9 Pro', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Pixel 9', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Pixel 8 Pro', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Pixel 8', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Pixel 8a', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Pixel 7 Pro', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'Pixel 7', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'Pixel 7a', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Pixel 6 Pro', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'Pixel 6', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'Pixel 6a', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'Pixel 5', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'Pixel 5a', esim: true, dual: true, fiveG: true, year: 2021 },
    { model: 'Pixel 4 XL', esim: true, dual: true, fiveG: false, year: 2019 },
    { model: 'Pixel 4', esim: true, dual: true, fiveG: false, year: 2019 },
    { model: 'Pixel 4a', esim: true, dual: true, fiveG: false, year: 2020 },
    { model: 'Pixel 3 XL', esim: true, dual: true, fiveG: false, year: 2018 },
    { model: 'Pixel 3', esim: true, dual: true, fiveG: false, year: 2018 },
    { model: 'Pixel 3a XL', esim: true, dual: true, fiveG: false, year: 2019 },
    { model: 'Pixel 3a', esim: true, dual: true, fiveG: false, year: 2019 },
  ],
  Huawei: [
    { model: 'P60 Pro', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'P50 Pro', esim: true, dual: true, fiveG: false, year: 2021 },
    { model: 'P40 Pro+', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'P40 Pro', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'P40', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'Mate 40 Pro', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'Mate Xs 2', esim: true, dual: true, fiveG: true, year: 2022 },
  ],
  Motorola: [
    { model: 'Razr+ (2024)', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Razr (2024)', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Razr+ (2023)', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Razr (2023)', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Edge 40 Pro', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Edge+ (2022)', esim: true, dual: true, fiveG: true, year: 2022 },
    { model: 'Razr 5G', esim: true, dual: true, fiveG: true, year: 2020 },
    { model: 'Razr (2019)', esim: true, dual: false, fiveG: false, year: 2019 },
  ],
  OnePlus: [
    { model: 'OnePlus 12', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'OnePlus 11', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'OnePlus Open', esim: true, dual: true, fiveG: true, year: 2023 },
  ],
  Xiaomi: [
    { model: '14 Ultra', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: '14 Pro', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: '13 Pro', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: '12T Pro', esim: true, dual: true, fiveG: true, year: 2022 },
  ],
  Sony: [
    { model: 'Xperia 1 VI', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Xperia 1 V', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Xperia 5 V', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Xperia 10 V', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Xperia 1 IV', esim: true, dual: true, fiveG: true, year: 2022 },
  ],
  OPPO: [
    { model: 'Find X7 Ultra', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Find X6 Pro', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Find N3', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Reno 10 Pro+', esim: true, dual: true, fiveG: true, year: 2023 },
  ],
  Honor: [
    { model: 'Magic6 Pro', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Magic5 Pro', esim: true, dual: true, fiveG: true, year: 2023 },
    { model: 'Magic V2', esim: true, dual: true, fiveG: true, year: 2023 },
  ],
  Nothing: [
    { model: 'Phone (2a)', esim: true, dual: true, fiveG: true, year: 2024 },
    { model: 'Phone (2)', esim: true, dual: true, fiveG: true, year: 2023 },
  ],
};

const BRANDS = Object.keys(ESIM_DEVICES).sort();

// Flatten all devices for search
const ALL_DEVICES = BRANDS.flatMap(brand =>
  ESIM_DEVICES[brand].map(d => ({ ...d, brand }))
);

function CompatibilityResult({ device, onReset }) {
  const { t } = useTranslation();
  if (!device) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6"
    >
      <div className={`rounded-2xl border p-6 ${
        device.esim
          ? 'bg-emerald-500/10 border-emerald-500/30'
          : 'bg-rose-500/10 border-rose-500/30'
      }`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            device.esim ? 'bg-emerald-500/20' : 'bg-rose-500/20'
          }`}>
            {device.esim
              ? <Check className="w-6 h-6 text-emerald-400" />
              : <X className="w-6 h-6 text-rose-400" />
            }
          </div>
          <div className="flex-1">
            <h3 className={`text-lg font-bold ${device.esim ? 'text-emerald-400' : 'text-rose-400'}`}>
              {device.esim ? t('compatibility.compatible') : t('compatibility.notCompatible')}
            </h3>
            <p className="text-white font-medium mt-1">
              {device.brand} {device.model}
            </p>

            {device.esim && (
              <div className="flex flex-wrap gap-3 mt-4">
                {device.fiveG && (
                  <span className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1 text-sm text-teal-300">
                    <Signal className="w-3.5 h-3.5" /> {t('compatibility.fiveGReady')}
                  </span>
                )}
                {device.dual && (
                  <span className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1 text-sm text-sky-300">
                    <Smartphone className="w-3.5 h-3.5" /> {t('compatibility.dualSim')}
                  </span>
                )}
                <span className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1 text-sm text-violet-300">
                  <Wifi className="w-3.5 h-3.5" /> eSIM
                </span>
              </div>
            )}

            {!device.esim && (
              <p className="text-slate-400 text-sm mt-3">
                {t('compatibility.notCompatibleDesc')}
              </p>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={onReset}
        className="mt-4 text-sm text-teal-400 hover:text-teal-300 transition-colors"
        data-testid="compatibility-reset"
      >
        {t('compatibility.checkAnother')}
      </button>
    </motion.div>
  );
}

export function DeviceCompatibilityChecker({ compact = false, onResult }) {
  const { t } = useTranslation();
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(true);
  const [result, setResult] = useState(null);
  const [isBrandOpen, setIsBrandOpen] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);

  const models = selectedBrand ? ESIM_DEVICES[selectedBrand] || [] : [];

  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const q = searchQuery.toLowerCase();
    return ALL_DEVICES
      .filter(d => `${d.brand} ${d.model}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [searchQuery]);

  const handleCheck = useCallback((device) => {
    setResult(device);
    onResult?.(device.esim);
  }, [onResult]);

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setSelectedModel('');
    setResult(null);
    setIsBrandOpen(false);
  };

  const handleModelSelect = (model) => {
    const device = models.find(m => m.model === model);
    setSelectedModel(model);
    setIsModelOpen(false);
    if (device) handleCheck({ ...device, brand: selectedBrand });
  };

  const handleSearchSelect = (device) => {
    setSearchQuery(`${device.brand} ${device.model}`);
    handleCheck(device);
  };

  const handleReset = () => {
    setSelectedBrand('');
    setSelectedModel('');
    setSearchQuery('');
    setResult(null);
    onResult?.(null);
  };

  if (compact) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="compatibility-checker-compact">
        <div className="flex items-center gap-2 mb-3">
          <Smartphone className="w-4 h-4 text-teal-400" />
          <span className="text-sm font-medium text-white">{t('compatibility.checkCompatibility')}</span>
        </div>

        {result ? (
          <div className={`flex items-center gap-2 p-2 rounded-lg ${result.esim ? 'bg-emerald-500/10' : 'bg-rose-500/10'}`}>
            {result.esim
              ? <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              : <X className="w-4 h-4 text-rose-400 flex-shrink-0" />
            }
            <span className={`text-sm ${result.esim ? 'text-emerald-400' : 'text-rose-400'}`}>
              {result.brand} {result.model} — {result.esim ? t('compatibility.compatible') : t('compatibility.notCompatible')}
            </span>
            <button onClick={handleReset} className="ml-auto text-slate-500 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('compatibility.searchDevice')}
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
              data-testid="compatibility-compact-search"
            />
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a2332] border border-white/10 rounded-lg shadow-2xl z-50 max-h-48 overflow-y-auto">
                {searchResults.map((d, i) => (
                  <button
                    key={i}
                    onClick={() => handleSearchSelect(d)}
                    className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-white/10 hover:text-white flex items-center justify-between"
                  >
                    <span>{d.brand} {d.model}</span>
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="compatibility-checker">
      {/* Mode toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { setShowSearch(true); handleReset(); }}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            showSearch ? 'bg-teal-500 text-black' : 'bg-white/5 text-slate-300 hover:bg-white/10'
          }`}
          data-testid="mode-search"
        >
          <Search className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          {t('compatibility.search')}
        </button>
        <button
          onClick={() => { setShowSearch(false); handleReset(); }}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            !showSearch ? 'bg-teal-500 text-black' : 'bg-white/5 text-slate-300 hover:bg-white/10'
          }`}
          data-testid="mode-select"
        >
          <Smartphone className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          {t('compatibility.selectDevice')}
        </button>
      </div>

      <AnimatePresence mode="wait">
        {showSearch ? (
          <motion.div key="search" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setResult(null); }}
                placeholder={t('compatibility.searchPlaceholder')}
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-4 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors text-lg"
                data-testid="compatibility-search-input"
              />
            </div>

            {searchResults.length > 0 && !result && (
              <div className="mt-2 bg-[#1a2332] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                {searchResults.map((d, i) => (
                  <button
                    key={i}
                    onClick={() => handleSearchSelect(d)}
                    className="w-full px-5 py-3 text-left hover:bg-white/10 flex items-center justify-between border-b border-white/5 last:border-0 transition-colors"
                    data-testid={`search-result-${i}`}
                  >
                    <div>
                      <span className="text-white font-medium">{d.brand}</span>
                      <span className="text-slate-400 ml-2">{d.model}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {d.fiveG && <span className="text-xs text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded">5G</span>}
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                  </button>
                ))}
              </div>
            )}

            {searchQuery.length >= 2 && searchResults.length === 0 && !result && (
              <div className="mt-4 flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <div>
                  <p className="text-amber-300 text-sm font-medium">{t('compatibility.deviceNotFound')}</p>
                  <p className="text-slate-400 text-xs mt-1">{t('compatibility.deviceNotFoundDesc')}</p>
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div key="select" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            {/* Brand selector */}
            <div className="relative">
              <button
                onClick={() => { setIsBrandOpen(!isBrandOpen); setIsModelOpen(false); }}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-left flex items-center justify-between hover:border-white/20 transition-colors"
                data-testid="brand-selector"
              >
                <span className={selectedBrand ? 'text-white' : 'text-slate-500'}>
                  {selectedBrand || t('compatibility.selectBrand')}
                </span>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isBrandOpen ? 'rotate-180' : ''}`} />
              </button>
              {isBrandOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a2332] border border-white/10 rounded-xl shadow-2xl z-[100] max-h-64 overflow-y-auto">
                  {BRANDS.map(brand => (
                    <button
                      key={brand}
                      onClick={() => handleBrandSelect(brand)}
                      className={`w-full px-5 py-3 text-left hover:bg-white/10 border-b border-white/5 last:border-0 transition-colors ${
                        selectedBrand === brand ? 'text-teal-400 bg-teal-500/5' : 'text-slate-300'
                      }`}
                      data-testid={`brand-${brand}`}
                    >
                      {brand}
                      <span className="text-slate-500 text-sm ml-2">({ESIM_DEVICES[brand].length})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Model selector */}
            {selectedBrand && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="relative">
                <button
                  onClick={() => { setIsModelOpen(!isModelOpen); setIsBrandOpen(false); }}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-left flex items-center justify-between hover:border-white/20 transition-colors"
                  data-testid="model-selector"
                >
                  <span className={selectedModel ? 'text-white' : 'text-slate-500'}>
                    {selectedModel || t('compatibility.selectModel')}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isModelOpen ? 'rotate-180' : ''}`} />
                </button>
                {isModelOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a2332] border border-white/10 rounded-xl shadow-2xl z-[100] max-h-64 overflow-y-auto">
                    {models.map((device, i) => (
                      <button
                        key={i}
                        onClick={() => handleModelSelect(device.model)}
                        className={`w-full px-5 py-3 text-left hover:bg-white/10 border-b border-white/5 last:border-0 transition-colors flex items-center justify-between ${
                          selectedModel === device.model ? 'text-teal-400 bg-teal-500/5' : 'text-slate-300'
                        }`}
                        data-testid={`model-${i}`}
                      >
                        <span>{device.model}</span>
                        <div className="flex items-center gap-2">
                          {device.fiveG && <span className="text-xs text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded">5G</span>}
                          <span className="text-xs text-slate-500">{device.year}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <CompatibilityResult device={result} onReset={handleReset} />
    </div>
  );
}

// Full page component
export default function DeviceCompatibilityPage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/10 rounded-2xl mb-4">
            <Smartphone className="w-8 h-8 text-teal-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-outfit" data-testid="compatibility-title">
            {t('compatibility.title')}
          </h1>
          <p className="text-slate-400 mt-3 text-base sm:text-lg">
            {t('compatibility.subtitle')}
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 relative z-10">
          <DeviceCompatibilityChecker />
        </div>

        <div className="mt-10 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8">
          <h2 className="text-xl font-bold text-white mb-4">{t('compatibility.howToCheck')}</h2>
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-teal-400 font-bold text-sm">1</span>
              </div>
              <div>
                <p className="text-white font-medium">iPhone</p>
                <p className="text-slate-400 text-sm">{t('compatibility.iphoneInstructions')}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-teal-400 font-bold text-sm">2</span>
              </div>
              <div>
                <p className="text-white font-medium">Android</p>
                <p className="text-slate-400 text-sm">{t('compatibility.androidInstructions')}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-teal-400 font-bold text-sm">3</span>
              </div>
              <div>
                <p className="text-white font-medium">{t('compatibility.quickCheck')}</p>
                <p className="text-slate-400 text-sm">{t('compatibility.quickCheckDesc')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <p className="text-slate-500 text-sm mb-4">{t('compatibility.brandsCount')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            {BRANDS.map(brand => (
              <span key={brand} className="text-slate-500 text-sm bg-white/5 px-3 py-1.5 rounded-full">
                {brand}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
